import type { Message } from './api'

export const READ_ALOUD_REQUEST_CHAR_LIMIT = 4096
export const READ_ALOUD_CHUNK_TARGET_CHARS = 3800

const INLINE_MARKER_PATTERN = /__(?:ASK_USER_QUESTION|ATTACHMENT_CHIPS|EMAIL_DRAFT_CARD|EMAIL_STAGED_DRAFT_CARD|EMAIL_DRAFT_FEEDBACK_CARD|PAY_CARD)__[\s\S]*$/g

export function messageTextForReadAloud(body: string): string {
  return body
    .replace(INLINE_MARKER_PATTERN, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/```[\s\S]*?```/g, ' Code block omitted. ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/[*_~>#|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function bestChunkBreak(text: string, maxChars: number): number {
  const windowStart = Math.max(0, maxChars - 900)
  const candidates = [
    text.lastIndexOf('\n\n', maxChars),
    text.lastIndexOf('\n', maxChars),
    text.lastIndexOf('. ', maxChars),
    text.lastIndexOf('? ', maxChars),
    text.lastIndexOf('! ', maxChars),
    text.lastIndexOf('; ', maxChars),
    text.lastIndexOf(', ', maxChars),
    text.lastIndexOf(' ', maxChars),
  ].filter((idx) => idx >= windowStart)
  return candidates.length > 0 ? Math.max(...candidates) + 1 : maxChars
}

export function splitReadAloudText(
  text: string,
  maxChars = READ_ALOUD_CHUNK_TARGET_CHARS,
): string[] {
  const cleaned = messageTextForReadAloud(text)
  if (!cleaned) return []
  const safeMax = Math.min(Math.max(1, maxChars), READ_ALOUD_REQUEST_CHAR_LIMIT)
  const chunks: string[] = []
  let remaining = cleaned
  while (remaining.length > safeMax) {
    const cut = bestChunkBreak(remaining, safeMax)
    const chunk = remaining.slice(0, cut).trim()
    if (chunk) chunks.push(chunk)
    remaining = remaining.slice(cut).trim()
  }
  if (remaining) chunks.push(remaining)
  return chunks
}

export function latestMessageForImmediateReadAloud(
  messages: readonly Message[] | null | undefined,
): Message | null {
  if (!messages) return null
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (!message || message.deleted || message.direction === 'system') continue
    if (!messageTextForReadAloud(message.body)) continue
    return message
  }
  return null
}
